<script setup lang="ts">
/**
 * Календарь задач — макет «Календарь» на странице «01 · Ядро».
 *
 * Экран отвечает на один вопрос: что и когда сгорает. Поэтому в сетке только
 * задачи со сроком — без срока задача в календаре не существует, и класть её
 * «на сегодня» значило бы выдумать за человека дату.
 *
 * Задачи те же, что в «Моих»: свои и те, за которыми следишь. Календарь всех
 * задач всех проектов был бы стеной, в которой не найти своё, а разрез по
 * проекту уже есть на доске.
 *
 * Рядом с задачами — встречи, куда звали (DEV-2217): трекер — единственное
 * место, где команде назначают созвоны, и месяц без них отвечал бы только на
 * половину вопроса «что у меня на неделе».
 */
import { AppButton, AppSegmentedControl } from '@vinprop/design-system'
import { AlertTriangle, ChevronLeft, ChevronRight, Video } from 'lucide-vue-next'
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import TrackerEmptyState from '@/components/TrackerEmptyState.vue'
import TrackerToolbar from '@/components/TrackerToolbar.vue'
import { dayKey, dueKey, monthGrid, shiftMonth } from '@/features/calendar/month'
import { listMeetingsBetween, type MeetingSession } from '@/features/meetings/api'
import { isOverdue, listMine, type MyIssue } from '@/features/my/api'
import IssueTypeIcon from '@/features/issues/components/IssueTypeIcon.vue'

/** Сколько строк влезает в клетку до «ещё N» — встречи и задачи вместе */
const PER_DAY = 4

/** Страница встреч — потолок сервера; месяц созвонов одного человека в неё помещается */
const MEETINGS_PAGE = 100

/** Строка клетки: встреча или задача. Одна лента, чтобы «ещё N» считало обе */
type DayItem = { meeting: MeetingSession; issue?: never } | { issue: MyIssue; meeting?: never }

const { t, locale } = useI18n()

const scope = ref<'mine' | 'watching'>('mine')
const month = ref(new Date())
const issues = ref<MyIssue[]>([])
const meetings = ref<MeetingSession[]>([])
const isLoading = ref(false)
const error = ref('')
/** Встречи не приехали. Календарь без них живёт — задачи важнее, чем пустой экран */
const meetingsFailed = ref(false)

const scopes = computed(() => [
  { value: 'mine' as const, label: t('calendar.scope.mine') },
  { value: 'watching' as const, label: t('calendar.scope.watching') },
])

const load = async () => {
  isLoading.value = true
  error.value = ''

  try {
    // Календарь берёт одну страницу в сотню строк: больше открытых задач со
    // сроком на месяц — уже не календарь, а завал (DEV-2045).
    const page = await listMine(scope.value === 'watching' ? 'watching' : 'assigned')

    issues.value = page.issues
  } catch {
    error.value = t('calendar.error.description')
  } finally {
    isLoading.value = false
  }
}

/** Дни сетки от первого понедельника до последнего воскресенья — они не зависят от задач */
const gridDays = computed(() => monthGrid<never>(month.value, new Map()).flat())

let meetingsRequest = 0

/**
 * Встречи за видимую сетку — окно `[первый день, день после последнего)`.
 * Страницы дочитываются до первой неполной: календарю нужен весь месяц.
 */
const loadMeetings = async () => {
  // Быстрое листание месяцев: ответ на прошлый месяц не должен перекрыть текущий.
  const request = ++meetingsRequest
  const first = gridDays.value[0]!.date
  const last = gridDays.value[gridDays.value.length - 1]!.date
  const to = new Date(last.getFullYear(), last.getMonth(), last.getDate() + 1)
  const rows: MeetingSession[] = []

  meetingsFailed.value = false

  try {
    for (let page = 1; ; page += 1) {
      const chunk = await listMeetingsBetween(first, to, page, MEETINGS_PAGE)

      rows.push(...chunk)

      if (chunk.length < MEETINGS_PAGE) break
    }

    if (request === meetingsRequest) meetings.value = rows
  } catch {
    if (request === meetingsRequest) meetingsFailed.value = true
  }
}

/**
 * Строки по дням: ключ — местная дата, а не UTC-срез. В дне сперва встречи по
 * времени — это расписание, — потом задачи: у срока времени по сути нет.
 */
const byDay = computed(() => {
  const map = new Map<string, DayItem[]>()
  const put = (key: string, item: DayItem) => {
    if (key) map.set(key, [...(map.get(key) ?? []), item])
  }

  // Отменённая встреча в календаре — ложная тревога: её уже не будет.
  const scheduled = meetings.value
    .filter((meeting) => meeting.meetingStatus !== 'CANCELLED')
    .sort((a, b) => Date.parse(a.scheduledStart) - Date.parse(b.scheduledStart))

  for (const meeting of scheduled) put(dueKey(meeting.scheduledStart), { meeting })
  for (const issue of issues.value) put(dueKey(issue.dueDate), { issue })

  return map
})

const weeks = computed(() => monthGrid(month.value, byDay.value))

/** Ключ строки: встреча и задача могут совпасть по id — это разные сущности */
const itemKey = (item: DayItem) => (item.meeting ? `m-${item.meeting.id}` : item.issue.id)

const timeOf = (meeting: MeetingSession) =>
  new Date(meeting.scheduledStart).toLocaleTimeString(locale.value, {
    hour: '2-digit',
    minute: '2-digit',
  })

// Месяц и год порознь: с `year: 'numeric'` русская локаль дописывает «г.»,
// а в заголовке экрана это лишнее слово.
const monthLabel = computed(
  () =>
    `${month.value.toLocaleDateString(locale.value, { month: 'long' })} ${month.value.getFullYear()}`,
)

/** Подписи дней недели берём у самой локали — переводить их вручную незачем */
const weekdays = computed(() => {
  const monday = new Date(2026, 7, 10)

  return Array.from({ length: 7 }, (_, index) =>
    new Date(2026, 7, 10 + index - monday.getDay() + 1).toLocaleDateString(locale.value, {
      weekday: 'short',
    }),
  )
})

const withoutDue = computed(() => issues.value.filter((issue) => !dueKey(issue.dueDate)).length)

const isEmptyMonth = computed(
  () => !isLoading.value && !error.value && weeks.value.every((week) => week.every((d) => !d.items.length)),
)

const today = () => {
  month.value = new Date()
}

const step = (direction: number) => {
  month.value = shiftMonth(month.value, direction)
}

onMounted(() => {
  void load()
  void loadMeetings()
})

watch(scope, load)
watch(month, loadMeetings)
</script>

<template>
  <TrackerToolbar :breadcrumbs="[{ label: t('views.calendar') }]">
    <AppSegmentedControl
      v-model="scope"
      :options="scopes"
      size="small"
      :aria-label="t('calendar.scope.label')"
    />

    <AppButton variant="secondary" size="small" @click="today">
      {{ t('calendar.today') }}
    </AppButton>
  </TrackerToolbar>

  <div class="cal">
    <header class="cal__head">
      <h1 class="cal__month">{{ monthLabel }}</h1>

      <div class="cal__months">
        <button
          type="button"
          class="cal__step"
          :aria-label="t('calendar.prev')"
          @click="step(-1)"
        >
          <ChevronLeft :size="14" />
        </button>
        <button type="button" class="cal__step" :aria-label="t('calendar.next')" @click="step(1)">
          <ChevronRight :size="14" />
        </button>
      </div>

      <span class="cal__spacer" />

      <!-- Задачи без срока в сетке места не имеют, но и молчать о них нельзя:
           иначе календарь выглядит как полный список, которым он не является. -->
      <p v-if="withoutDue" class="cal__note">
        {{ t('calendar.withoutDue', { count: withoutDue }) }}
      </p>

      <p v-if="meetingsFailed" class="cal__note">{{ t('calendar.meetingsError') }}</p>
    </header>

    <TrackerEmptyState
      v-if="error"
      :icon="AlertTriangle"
      :title="t('calendar.error.title')"
      :description="error"
      tint="var(--color-danger-tint)"
    >
      <AppButton variant="secondary" @click="load">{{ t('board.error.retry') }}</AppButton>
    </TrackerEmptyState>

    <template v-else>
      <div class="cal__weekdays">
        <span v-for="day in weekdays" :key="day" class="cal__weekday">{{ day }}</span>
      </div>

      <div class="cal__grid">
        <div v-for="(week, index) in weeks" :key="index" class="cal__week">
          <div
            v-for="day in week"
            :key="dayKey(day.date)"
            class="cal__day"
            :class="{ 'cal__day--outside': day.outside, 'cal__day--today': day.today }"
          >
            <span class="cal__number">{{ day.day }}</span>

            <template v-for="item in day.items.slice(0, PER_DAY)" :key="itemKey(item)">
              <RouterLink
                v-if="item.meeting"
                class="cal__meeting"
                :to="`/meetings/${item.meeting.id}`"
                :title="item.meeting.title"
              >
                <Video :size="12" aria-hidden="true" />
                <span class="cal__time">{{ timeOf(item.meeting) }}</span>
                <span class="cal__title">{{ item.meeting.title }}</span>
              </RouterLink>

              <RouterLink
                v-else
                class="cal__task"
                :class="{ 'cal__task--overdue': isOverdue(item.issue) }"
                :to="`/issue/${item.issue.key}`"
                :title="item.issue.title"
              >
                <IssueTypeIcon :type="item.issue.type" :size="12" />
                <span class="cal__key">{{ item.issue.key }}</span>
                <span class="cal__title">{{ item.issue.title }}</span>
              </RouterLink>
            </template>

            <span v-if="day.items.length > PER_DAY" class="cal__more">
              {{ t('calendar.more', { count: day.items.length - PER_DAY }) }}
            </span>
          </div>
        </div>
      </div>

      <p v-if="isEmptyMonth" class="cal__empty">{{ t('calendar.empty') }}</p>
    </template>
  </div>
</template>

<style scoped>
/* Экран ровно по высоте окна: сетка месяца — это таблица, и прокручивать её
   целиком незачем, а клетки тянутся по остатку. */
.cal {
  display: flex;
  flex-direction: column;
  gap: 12px;
  height: calc(100vh - 48px);
  padding: 20px 24px;
}

.cal__head {
  display: flex;
  align-items: center;
  gap: 12px;
}

.cal__month {
  margin: 0;
  color: var(--color-text-primary);
  font-size: var(--font-size-h3);
  font-weight: var(--font-weight-semibold);
  text-transform: capitalize;
}

.cal__months {
  display: flex;
  gap: 4px;
}

.cal__step {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  color: var(--color-text-secondary);
  cursor: pointer;
}

.cal__step:hover {
  border-color: var(--color-border-strong);
  color: var(--color-text-primary);
}

.cal__spacer {
  flex: 1;
}

.cal__note {
  margin: 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-body-sm);
}

.cal__weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 8px;
}

.cal__weekday {
  color: var(--color-text-muted);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-overline);
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.cal__grid {
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 8px;
  min-height: 0;
}

.cal__week {
  display: grid;
  flex: 1;
  grid-template-columns: repeat(7, 1fr);
  gap: 8px;
  min-height: 0;
}

.cal__day {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  padding: 6px 8px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  background: var(--color-surface);
  overflow: hidden;
}

.cal__day--outside {
  background: var(--color-bg-muted);
}

.cal__day--today {
  border-color: var(--color-primary);
}

.cal__number {
  color: var(--color-text-primary);
  font-size: var(--font-size-body-sm);
  font-weight: var(--font-weight-medium);
}

.cal__day--outside .cal__number {
  color: var(--color-text-muted);
}

.cal__task {
  display: flex;
  align-items: center;
  gap: 5px;
  min-width: 0;
  padding: 3px 6px;
  border-radius: var(--radius-sm);
  background: var(--color-bg-muted);
  color: var(--color-text-primary);
  font-size: var(--font-size-caption);
  text-decoration: none;
}

.cal__task:hover {
  background: var(--color-border);
}

/* Встреча — та же плашка, но в цвете акцента: в клетке она должна читаться
   как «созвон», а не как ещё одна задача. */
.cal__meeting {
  display: flex;
  align-items: center;
  gap: 5px;
  min-width: 0;
  padding: 3px 6px;
  border-radius: var(--radius-sm);
  background: var(--color-primary-tint);
  color: var(--color-text-primary);
  font-size: var(--font-size-caption);
  text-decoration: none;
}

.cal__meeting:hover {
  color: var(--color-primary);
}

.cal__meeting > svg {
  flex-shrink: 0;
  color: var(--color-primary);
}

.cal__key,
.cal__time {
  flex-shrink: 0;
  color: var(--color-text-muted);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-overline);
}

/* Просроченное видно ключом: заливка всей плашки красным на пяти задачах
   подряд превращает месяц в тревогу. */
.cal__task--overdue .cal__key {
  color: var(--color-danger);
}

.cal__title {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cal__more {
  color: var(--color-text-muted);
  font-size: var(--font-size-overline);
}

.cal__empty {
  margin: 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-body-sm);
}
</style>
